import { Box, Stack, Typography } from "@mui/material"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import type { ReactNode } from "react"

import { GRID_LAYOUT_GAP, SECTIONS_BORDER_RADIUS } from "helpers/utils/constants"
import CustomLink from "widgets/common/CustomLink"

interface NoticePanelProps {
  /** Only a state that has to be established first earns one. */
  title?: string
  body: ReactNode
  action?: { label: string; href: string }
}

/**
 * A note above a dashboard saying why it does not hold what you expected, and
 * where to go instead. Sits in the slot a chart would, so it reads as part of
 * the grid rather than as a banner over it.
 */
const NoticePanel = ({ title, body, action }: NoticePanelProps) => (
  <Box
    sx={{
      p: 2,
      // The chart grid sets this same gap above its first row, so the panel
      // sits the same distance from the tabs as a chart would.
      mt: `${GRID_LAYOUT_GAP}px`,
      // The plot card's own surface, marked only by the accent stripe.
      backgroundColor: "background.paper",
      borderLeft: "3px solid",
      borderLeftColor: "info.main",
      borderRadius: SECTIONS_BORDER_RADIUS,
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: { xs: "stretch", md: "center" },
      gap: 2,
    }}
  >
    <Stack
      direction="row"
      spacing={1.5}
      alignItems={title ? "flex-start" : "center"}
      sx={{ flex: 1, minWidth: 0 }}
    >
      {/* Neutral, not the stripe's colour: none of these states is a fault. */}
      <InfoOutlinedIcon
        fontSize="small"
        sx={{ color: "text.secondary", flexShrink: 0, mt: title ? 0.25 : 0 }}
      />
      <Box>
        {title && <Typography fontWeight={600}>{title}</Typography>}
        <Typography color="text.secondary">{body}</Typography>
      </Box>
    </Stack>
    {action && (
      // The anchor is the flex child, so the alignment sits on the box around it.
      <Box sx={{ alignSelf: "center" }}>
        <CustomLink
          href={action.href}
          component="span"
          bold
          underlineHover
          styles={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          {action.label}
          <ChevronRightIcon fontSize="small" />
        </CustomLink>
      </Box>
    )}
  </Box>
)

export default NoticePanel
